import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMenuPermission } from '../../hooks/useMenuPermission';
import { Button, Card, Dropdown, Tag, message } from 'antd';
import {
  AimOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  EditOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  LaptopOutlined,
  MoreOutlined,
  SolutionOutlined,
} from '@ant-design/icons';
import { getFormGroups } from '../../services/addFormApi';
import { getModuleActions } from '../../services/detailedViewApi';
import { fetchJsonWithAuth } from '../../services/authApi';
import { AUTH_URL } from '../../services/apiConfig';
import { loadDetailDefaults } from '../../services/detailDefaults';
import { extractDocuments } from './renderConfig';
import { buildHeaderFromGroups } from './detailConfig/headerConfig';
import DocumentViewer from '../DocumentViewer';
import '../../styles/detail-header-card.css';

// String icon keys (from headerConfig) → icon elements.
const ICONS = {
  location: <EnvironmentOutlined />,
  designation: <SolutionOutlined />,
  experience: <ClockCircleOutlined />,
  employment: <LaptopOutlined />,
  rate: <DollarOutlined />,
  source: <AimOutlined />,
  resume: <FileTextOutlined />,
  edit: <EditOutlined />,
  approve: <CheckCircleOutlined />,
  reject: <CloseCircleOutlined />,
};

// Build a flat record from the fetched form-groups so route templates such as
// "/app/candidates/edit/:_id" can be filled with the record's real values.
function recordFromGroups(formGroups, id) {
  const record = { _id: id, id };
  for (const group of formGroups ?? []) {
    for (const field of group.fields ?? []) {
      if (field?.field != null && field.value !== undefined) record[field.field] = field.value;
    }
  }
  return record;
}

function templatePart(value) {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join(',');
  if (typeof value === 'object') return String(value.id ?? value.value ?? '');
  return String(value);
}

// Fill ":_id", ":field" and "{field}" placeholders in a route template.
function fillRouteTemplate(template, record) {
  return String(template)
    .replace(/\{([^}]+)\}/g, (_, key) => encodeURIComponent(templatePart(record?.[key])))
    .replace(/:([A-Za-z_][\w]*)/g, (_, key) => encodeURIComponent(templatePart(record?.[key])));
}

function getNestedValue(record, path) {
  return String(path || '').split('.').reduce((value, key) => value?.[key], record);
}

function comparable(value) {
  return String(value ?? '').trim().toLowerCase();
}

function conditionMatches(condition, record) {
  const actual = getNestedValue(record, condition?.field);
  switch (condition?.operator) {
    case 'eq': return comparable(actual) === comparable(condition.value);
    case 'ne': return comparable(actual) !== comparable(condition.value);
    case 'isEmpty': return actual == null || actual === '' || (Array.isArray(actual) && !actual.length);
    case 'notEmpty': return !(actual == null || actual === '' || (Array.isArray(actual) && !actual.length));
    case 'fieldEq': return comparable(actual) === comparable(getNestedValue(record, condition.compareField));
    case 'contains': return comparable(actual).includes(comparable(condition.value));
    case 'scope': return comparable(condition.value) === 'detail';
    default: return false;
  }
}

function isBlocked(action, record, rules) {
  return rules.some((rule) => (
    rule?.isActive !== false
    && (rule?.blockedActions ?? []).map(String).includes(String(action.key))
    && (rule?.conditions ?? []).every((condition) => conditionMatches(condition, record))
  ));
}

function normalizeConfiguredAction(action) {
  const key = String(action.key ?? '');
  return {
    ...action,
    key,
    label: action.label || key,
    route: action.route || action.routeTemplate || (
      action.actionType === 'modal' && (action.popupKey || key)
        ? `/popup/${action.popupKey || key}`
        : ''
    ),
  };
}

function roleAllowsAction(permission, moduleName) {
  if (!permission) return true;
  try {
    const permissions = JSON.parse(localStorage.getItem('menuPermission') || '{}');
    const [configuredModule, key] = String(permission).split('.');
    const moduleKey = configuredModule || String(moduleName || '').replace(/s$/i, '');
    const modulePermissions = permissions[moduleKey]
      ?? permissions[String(moduleName || '')]
      ?? permissions[String(moduleName || '').replace(/s$/i, '')]
      ?? {};
    const value = modulePermissions[key];
    return value === undefined || value === null || (value !== false && value !== 0 && value !== '0');
  } catch {
    return true;
  }
}

// Resolve document objects from the form-groups for the DocumentViewer.
// `fieldName` is the preferred field key (e.g. 'resume'); falls back to any
// resume/CV-looking field label or key.
function resolveDocuments(formGroups, fieldName) {
  const fields = (formGroups ?? []).flatMap((group) => group.fields ?? []);
  const target =
    fields.find((field) => field.field === fieldName) ??
    fields.find((field) => /resume|cv/i.test(`${field.field ?? ''} ${field.label ?? ''}`));
  if (!target) return [];
  return extractDocuments(target.value);
}

/*
 * Dynamic detail-view header. Self-contained: given `moduleName` + `id` it
 * fetches its own form-groups and builds the header object via
 * headerConfig.buildHeaderFromGroups — the page only passes moduleName + id.
 *
 *  moduleName, id  : forwarded back to onAction / onTabChange
 *  customConfigName : optional config key used only for rendering the card
 *  onAction(key, { moduleName, id })  : button + more-menu clicks
 *                                       (custom parent-level handling)
 *  onTabChange(key, { moduleName, id }) : nav tab clicks
 *
 * Route-based actions (action.route defined in module config) are navigated
 * directly here so the click → navigate path is as short as possible.
 */
export default function DetailHeaderCard({
  moduleName,
  id,
  customConfigName,
  onAction,
  onTabChange,
  onHeaderData,
  hideActions = false,
  defaultTab,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [formGroups, setFormGroups] = useState([]);
  const [configuredActions, setConfiguredActions] = useState([]);
  const [actionRules, setActionRules] = useState([]);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerDocs, setViewerDocs] = useState([]);
  const configName = customConfigName || moduleName;

  useEffect(() => {
    if (!moduleName) return undefined;

    let cancelled = false;

    async function fetchFormGroups() {
      try {
        // loadDetailDefaults() (cached) ensures resume/document URLs resolve
        // against the admin-configured S3 base, same as DetailViewV1.
        const [data, actionConfig] = await Promise.all([
          getFormGroups({ module: moduleName, id }),
          getModuleActions(moduleName).catch(() => ({ actions: [], actionRules: [] })),
          loadDetailDefaults(),
        ]);
        if (!cancelled) {
          setFormGroups(Array.isArray(data) ? data : []);
          setConfiguredActions(actionConfig.actions ?? []);
          setActionRules(actionConfig.actionRules ?? []);
        }
      } catch (err) {
        console.error(`Failed to fetch ${moduleName} header fields:`, err);
        if (!cancelled) {
          setFormGroups([]);
          setConfiguredActions([]);
          setActionRules([]);
        }
      }
    }

    fetchFormGroups();

    return () => { cancelled = true; };
  }, [moduleName, id]);

  const header = useMemo(
    () => buildHeaderFromGroups(configName, formGroups),
    [configName, formGroups],
  );

  useEffect(() => {
    if (header && onHeaderData) onHeaderData(header);
  }, [header]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    eyebrow,
    title = '-',
    status,
    titleIds = [],
    meta = [],
    ids = [],
    tabs = [],
    stats = [],
  } = header ?? {};

  const { canAdd, canEdit, canDelete } = useMenuPermission(moduleName);

  const record = useMemo(() => recordFromGroups(formGroups, id), [formGroups, id]);
  const detailActions = useMemo(() => configuredActions
    .filter((action) => action.showInDetail ?? action.showInList)
    .map(normalizeConfiguredAction)
    .filter((action) => !isBlocked(action, record, actionRules)),
  [actionRules, configuredActions, record]);

  function isActionAllowed(action) {
    const pk = action.permissionKey ?? (action.danger ? 'delete' : null);
    if (pk === 'add'    && !canAdd)    return false;
    if (pk === 'edit'   && !canEdit)   return false;
    if (pk === 'delete' && !canDelete) return false;
    if (!roleAllowsAction(action.permission, moduleName)) return false;
    if (action.permissionKey) {
      const rowPermission = getNestedValue(record, `actionPermission.${action.permissionKey}`);
      if (rowPermission === false || rowPermission === 0 || rowPermission === '0') return false;
    }
    return true;
  }

  // Own the active tab locally so a click highlights immediately — waiting on
  // defaultTab to round-trip back through the parent (URL state, re-fetches)
  // let the highlight snap back to the first tab while content had already
  // switched underneath it.
  const [localActiveTab, setLocalActiveTab] = useState(defaultTab);
  useEffect(() => { setLocalActiveTab(defaultTab); }, [defaultTab]);
  const activeTab = tabs.some((tab) => tab.key === localActiveTab)
    ? localActiveTab
    : tabs[0]?.key;

  // Admin can restrict an action to selected Detail tabs with allowedTabs.
  const isAllowedOnTab = (action) => !action.allowedTabs?.length
    || action.allowedTabs.some((tab) => comparable(tab) === comparable(activeTab));
  const visiblePrimaryActions = detailActions.filter(
    (action) => action.detailPlacement === 'primary' && isActionAllowed(action) && isAllowedOnTab(action),
  );
  const visibleMoreActions = detailActions.filter(
    (action) => action.detailPlacement !== 'primary' && isActionAllowed(action) && isAllowedOnTab(action),
  );

  if (!header) return null;

  const fireAction = (key) => {
    const action = [...visiblePrimaryActions, ...visibleMoreActions].find((a) => a.key === key);

    // Open a document (e.g. resume) in the DocumentViewer modal.
    if (action?.actionType === 'document' && action.documentField) {
      const docs = resolveDocuments(formGroups, action.documentField);
      if (docs.length) {
        setViewerDocs(docs);
        setViewerOpen(true);
      } else {
        message.warning(`No ${(action.label || 'document').toLowerCase()} available`);
      }
      return;
    }

    if (action?.actionType === 'copy') {
      const value = getNestedValue(record, action.copyField || 'id');
      navigator.clipboard.writeText(templatePart(value))
        .then(() => message.success('Copied to clipboard'))
        .catch(() => message.error('Could not copy to clipboard'));
      return;
    }

    if (action?.actionType === 'api' && action.apiPath) {
      const path = fillRouteTemplate(action.apiPath, record);
      const method = String(action.apiMethod || 'POST').toUpperCase();
      fetchJsonWithAuth(AUTH_URL, path.startsWith('/') ? path : `/${path}`, {
        method,
        body: ['GET', 'DELETE'].includes(method) ? undefined : JSON.stringify({ ids: [id], record }),
      })
        .then(() => message.success(`${action.label} completed`))
        .catch((error) => message.error(error.message || `${action.label} failed`));
      return;
    }

    if (action?.route) {
      const resolvedRoute = typeof action.route === 'function'
        ? action.route(id)
        : fillRouteTemplate(action.route, recordFromGroups(formGroups, id));

      if (resolvedRoute.startsWith('/popup/')) {
        navigate(resolvedRoute, {
          state: {
            background: location,
            moduleName,
            selectedIds: [id],
            selectedRecords: [record],
            popupContext: action.popupState ?? {},
            popupAction: { key: action.key, apiMethod: action.apiMethod, apiPath: action.apiPath, defaultTemplateId: action.defaultTemplateId },
            ...action.popupState,
          },
        });
      } else {
        navigate(resolvedRoute);
      }
      return;
    }

    // No route — bubble up to parent for custom handling.
    onAction?.(key, { moduleName, id, action });
  };

  const selectTab = (key) => {
    setLocalActiveTab(key);
    onTabChange?.(key, { moduleName, id });
  };

  // Each item carries its own onClick so we don't rely on the global menu.onClick.
  const moreMenuItems = visibleMoreActions.map((action) => ({
    key: action.key,
    label: action.label,
    danger: action.danger,
    onClick: () => fireAction(action.key),
  }));

  return (
    <>
    <Card className="znx-dh-card">
      <div className="znx-dh-top">
        <div className="znx-dh-left">
          {eyebrow && <div className="znx-dh-eyebrow">{eyebrow}</div>}

          <div className="znx-dh-titlerow">
            <span className="znx-dh-title">{title}</span>
              {titleIds.map((item) => (
              <span className="znx-dh-titleid" key={item.label}>
                <span className="znx-dh-id-label">{item.label}:</span> {item.value}
              </span>
            ))}
            {status && (
              // <Tag color={status.color} className="znx-dh-status">
              <Tag
                color={status.color}
                className="znx-dh-status"
                style={{
                  backgroundColor: status.backgroundColor ?? status.color,
                  borderColor: status.backgroundColor ?? status.color,
                  color: status.textColor,
                }}
              >
                {status.label}
              </Tag>
            )}

          
          </div>

          {ids.length > 0 && (
            <div className="znx-dh-ids">
              {ids.map((item) => (
                <span className="znx-dh-id" key={item.label}>
                  <span className="znx-dh-id-label">{item.label}:</span> {item.value}
                </span>
              ))}
            </div>
          )}

          {meta.length > 0 && (
            <div className="znx-dh-meta">
              {meta.map((item, index) => (
                <span className="znx-dh-meta-item" key={index}>
                  {item.icon && ICONS[item.icon] && (
                    <span className="znx-dh-meta-icon">{ICONS[item.icon]}</span>
                  )}
                  <span>{item.text}</span>
                </span>
              ))}
            </div>
          )}
        </div>

        {!hideActions && (
          <div className="znx-dh-actions-col">
            <div className="znx-dh-actions">
              {visiblePrimaryActions.map((action) => (
                <Button
                  key={action.key}
                  type="text"
                  className={`znx-dh-btn znx-dh-btn--${action.tone ?? 'primary'}`}
                  icon={action.icon ? ICONS[action.icon] : undefined}
                  onClick={() => fireAction(action.key)}
                >
                  {action.label}
                </Button>
              ))}

              {visibleMoreActions.length > 0 && (
                <Dropdown
                  trigger={['click']}
                  placement="bottomRight"
                  menu={{ items: moreMenuItems }}
                >
                  <Button
                    type="text"
                    className="znx-dh-more"
                    icon={<MoreOutlined />}
                  />
                </Dropdown>
              )}
            </div>

            {stats.length > 0 && (
              <div className="znx-dh-stats">
                {stats.map((stat) => (
                  <div key={stat.label} className="znx-dh-stat">
                    <span className="znx-dh-stat-value">{stat.value}</span>
                    <span className="znx-dh-stat-label">{stat.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {tabs.length > 0 && (
        <div className="znx-dh-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`znx-dh-tab${activeTab === tab.key ? ' is-active' : ''}`}
              onClick={() => selectTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
    </Card>

    <DocumentViewer
      documents={viewerDocs}
      open={viewerOpen}
      onClose={() => setViewerOpen(false)}
    />
    </>
  );
}
